/**
 * React Query hooks for AIWorkflows API
 *
 * Provides type-safe hooks for all workflow CRUD, execution,
 * and schedule operations backed by the AIWorkflows microservice.
 */

import { useMemo } from 'react'
import { useQuery, useMutation, useQueryClient, useInfiniteQuery, keepPreviousData, type QueryClient } from '@tanstack/react-query'
import { queryKeys } from '../../lib/react-query'
import { useDeploymentRunScope } from '../../features/workflows/deploymentRunScope'
import {
  aiworkflowsApi,
  type WorkflowCreatePayload,
  type WorkflowUpdatePayload,
  type WorkflowReadResponse,
  type WorkflowTemplateCreatePayload,
  type WorkflowFromTemplateCreatePayload,
  type ExecutionReadResponse,
  type NodeExecutionReadResponse,
  type ScheduleCreatePayload,
  type WorkflowImportResult,
  type WorkflowPackageImportResult,
  type WorkflowPackageImportOptions,
  type WorkflowDependenciesResponse,
  type QuickFixRequest,
  type QuickFixResponse,
  type ResumeExecutionRequest,
  type ResumeStrategy,
  type TriggerUpsertPayload,
  type TriggerStatusUpdatePayload,
  type WorkflowControlStatusUpdatePayload,
  type TriggerTokenCreatePayload,
  type TriggerTokenUpdatePayload,
  type N8nImportRequest,
  type N8nImportResponse,
  type WorkflowVersionItem,
  type WorkflowNewVersionResponse,
} from '../../services/api/aiworkflows'
import { exportImportApi } from '../../services/api/exportImport'
import {
  downloadAsFile,
  generateExportFilename,
  parseYAML,
  toYAML,
} from '../../utils/yamlParser'
import { toast } from '../../utils/toast'
import { WORKFLOW_PAYLOAD_TOO_LARGE_MESSAGE } from '../../constants/entityLimits'
import {
  markExecutionStoppedByUser,
  WORKFLOW_STOPPED_TOAST_MESSAGE,
} from '../../features/workflows/utils/workflowExecutionCancelIntent'
import { ApiError } from '../../services/api/core'
import { isNotFoundError, isPermissionDeniedError } from '../../utils/apiErrorHandler'
import type { WorkflowStatus, WorkflowNode, WorkflowEdge } from '../../features/workflows/types'
import type { OpenAPISpec } from '../../components/OpenApiRenderer/types/openapi'
import { mergeWorkflowDetailWithCache } from '../../features/workflows/utils/workflowDetailCacheMerge'

/** Keep polling on 401/403/503; stop only on terminal 404 (project/workflow gone). */
export function executionsRefetchInterval(query: { state: { error: unknown } }): number | false {
  if (isNotFoundError(query.state.error)) return false
  return 15_000
}

function applyNewDraftVersionToListCaches(
  queryClient: QueryClient,
  workflowId: string,
  data: WorkflowNewVersionResponse,
) {
  // List caches only — never patch workflow detail. Updating current_version_id
  // without the new graph/lock_version unlocks the canvas on the previous
  // version's diagram and lets autosave overwrite the draft just created.
  queryClient.setQueryData<WorkflowVersionItem[]>(
    queryKeys.workflows.versions(workflowId),
    (old) => {
      const newVersion: WorkflowVersionItem = {
        id: data.version_id,
        workflow_id: workflowId,
        version_number: data.version_number,
        is_immutable: false,
      }
      if (old?.some((v) => v.id === data.version_id)) return old
      return [newVersion, ...(old ?? [])]
    },
  )

  queryClient.setQueriesData<{ items: WorkflowReadResponse[]; total: number }>(
    { queryKey: queryKeys.workflows.lists() },
    (old) => {
      if (!old?.items) return old
      return {
        ...old,
        items: old.items.map((w) =>
          w.id === workflowId
            ? {
                ...w,
                version_number: data.version_number,
                current_version_is_immutable: false,
              }
            : w,
        ),
      }
    },
  )
}

async function invalidateWorkflowVersionQueries(queryClient: QueryClient, workflowId: string) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(workflowId) }),
    queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() }),
    queryClient.invalidateQueries({ queryKey: queryKeys.workflows.versions(workflowId) }),
  ])
}

// =============================================================================
// WORKFLOW QUERIES
// =============================================================================

/** Fetch all workflows with optional filters */
export function useWorkflows(filters?: {
  /** Multi-select list filters pass an array; a bare value still works. */
  project_id?: string | string[]
  status?: WorkflowStatus | WorkflowStatus[]
  search?: string
  skip?: number
  limit?: number
  created_by?: string | string[]
  scheduled?: boolean
  schedule_active?: boolean
  schedule_states?: string[]
  created_at_from?: number
  created_at_to?: number
  last_run_at_from?: number
  last_run_at_to?: number
  last_run_status?:
    | 'success' | 'failed' | 'running' | 'never_run'
    | ('success' | 'failed' | 'running' | 'never_run')[]
  tags?: string[]
  order_by?: 'name' | 'status' | 'created_at' | 'updated_at' | 'created_by' | 'last_run_at'
  order_desc?: boolean
  fields?: string
  include_execution?: boolean
}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: queryKeys.workflows.list(filters as Record<string, unknown> | undefined),
    queryFn: () => aiworkflowsApi.listWorkflows(filters),
    placeholderData: keepPreviousData,
    enabled: options?.enabled ?? true,
    staleTime: 30_000, // 30s
    refetchOnMount: true, // Refetch on mount if stale (e.g., invalidated while inactive)
  })
}

/** Fetch a single workflow by ID */
export function useWorkflow(
  id: string | undefined,
  options?: {
    staleTime?: number
    refetchOnMount?: boolean | 'always'
    refetchInterval?: number | false | ((query: { state: { data?: WorkflowReadResponse } }) => number | false)
    triggerSource?: string
    deploymentEnvironment?: string
  }
) {
  const queryClient = useQueryClient()
  const triggerSource = options?.triggerSource
  const deploymentEnvironment = options?.deploymentEnvironment

  return useQuery({
    queryKey: triggerSource || deploymentEnvironment
      ? [...queryKeys.workflows.detail(id!), triggerSource ?? '', deploymentEnvironment ?? '']
      : queryKeys.workflows.detail(id!),
    queryFn: async () => {
      const server = await aiworkflowsApi.getWorkflow(id!, {
        triggerSource,
        ...(deploymentEnvironment ? { deploymentEnvironment } : {}),
      })
      const cached = queryClient.getQueryData<WorkflowReadResponse>(
        queryKeys.workflows.detail(id!)
      )
      // Store-scoped responses omit the full graph — never merge into / overwrite
      // the canvas detail cache with a redacted payload.
      if (triggerSource === 'workflow_store' || deploymentEnvironment) {
        return server
      }
      return mergeWorkflowDetailWithCache(cached, server)
    },
    enabled: !!id,
    staleTime: options?.staleTime ?? 15_000,
    refetchOnMount: options?.refetchOnMount,
    refetchInterval: options?.refetchInterval,
  })
}

/**
 * Lightweight status poll for the open canvas. Returns run-status fields only
 * (no graph_definition / trigger_config / creator enrichment) via fields=status,
 * so polling the canvas every few seconds cannot overwrite in-flight edits and
 * does not ship the full graph each tick. Kept on a SEPARATE query key from
 * useWorkflow so it never feeds the canvas nodes/edges props.
 */
export function useWorkflowStatus(
  id: string | undefined,
  options?: {
    refetchInterval?:
      | number
      | false
      | ((query: {
          state: { data?: WorkflowReadResponse; error: unknown }
        }) => number | false)
  }
) {
  return useQuery({
    queryKey: queryKeys.workflows.status(id!),
    queryFn: () => aiworkflowsApi.getWorkflowStatus(id!),
    enabled: !!id,
    staleTime: 0,
    refetchInterval: options?.refetchInterval,
  })
}

/**
 * Fetch the AIWorkflows OpenAPI spec (used by the workflow Docs tab).
 * Cached so revisiting the Docs tab is instant instead of re-fetching + flashing a spinner.
 */
export function useWorkflowsOpenApiSpec(options?: { enabled?: boolean }) {
  return useQuery<OpenAPISpec>({
    queryKey: queryKeys.workflows.openApiSpec(),
    queryFn: () => aiworkflowsApi.getOpenApiJson(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000, // 5min — the spec rarely changes within a session
  })
}

/** Per-workflow input_data / execution reference for Run with Params and Docs tab. */
export function useWorkflowExecutionReference(
  workflowId: string | undefined,
  options?: { enabled?: boolean; version?: 'published' | 'current' }
) {
  const version = options?.version ?? 'published'
  return useQuery({
    queryKey: queryKeys.workflows.executionReference(workflowId!, version),
    queryFn: () => aiworkflowsApi.getWorkflowExecutionReference(workflowId!, { version }),
    enabled: (options?.enabled ?? true) && !!workflowId,
    staleTime: 60_000,
  })
}

// =============================================================================
// WORKFLOW MUTATIONS
// =============================================================================

/** Create a new workflow */
export function useCreateWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: WorkflowCreatePayload) => aiworkflowsApi.createWorkflow(data),
    onSuccess: (data) => {
      queryClient.setQueriesData<{ items: WorkflowReadResponse[]; total: number }>(
        { queryKey: queryKeys.workflows.lists() },
        (old) => {
          if (!old?.items) return old
          return { ...old, items: [data, ...old.items], total: old.total + 1 }
        }
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      toast.success('Workflow created')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 413) {
        toast.error(ae.message || WORKFLOW_PAYLOAD_TOO_LARGE_MESSAGE)
        return
      }
      toast.error(error?.message || 'Failed to create workflow')
    },
  })
}

/** Create a new workflow from a Solution Template */
export function useCreateWorkflowFromTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: WorkflowFromTemplateCreatePayload) =>
      aiworkflowsApi.createWorkflowFromTemplate(data),
    onSuccess: (data) => {
      queryClient.setQueriesData<{ items: WorkflowReadResponse[]; total: number }>(
        { queryKey: queryKeys.workflows.lists() },
        (old) => {
          if (!old?.items) return old
          return { ...old, items: [data, ...old.items], total: old.total + 1 }
        }
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.templates() })
      toast.success('Workflow created from template')
      if (data.warnings?.length) {
        const moreWarnings =
          data.warnings.length > 3 ? `\n…and ${data.warnings.length - 3} more` : ''
        toast.warning(
          `Some node references could not be resolved:\n${data.warnings.slice(0, 3).join('\n')}${moreWarnings}`,
          { duration: 8000 }
        )
      }
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to create workflow from template')
    },
  })
}

const WORKFLOW_LOCK_CONFLICT_RE =
  /modified by another user|lock_version is required/i

export function isWorkflowLockConflict(error: ApiError): boolean {
  return error.status === 409 || (error.status === 422 && WORKFLOW_LOCK_CONFLICT_RE.test(error.message ?? ''))
}

/** Update an existing workflow */
export function useUpdateWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      id,
      data,
      silent,
    }: {
      id: string
      data: WorkflowUpdatePayload
      silent?: boolean
    }) => aiworkflowsApi.updateWorkflow(id, data, silent),
    onSuccess: (data, variables) => {
      queryClient.setQueryData<WorkflowReadResponse>(
        queryKeys.workflows.detail(variables.id),
        (prev) => {
          if (!prev) return data
          if (variables.silent) {
            const sent = variables.data
            const merged = {
              ...prev,
              lock_version: data.lock_version,
              updated_at: data.updated_at,
              graph_definition: sent.graph_definition ?? prev.graph_definition,
              trigger_config: sent.trigger_config ?? data.trigger_config ?? prev.trigger_config,
              execution_config: sent.execution_config ?? prev.execution_config,
            }
            return mergeWorkflowDetailWithCache(prev, merged)
          }
          return mergeWorkflowDetailWithCache(prev, { ...prev, ...data })
        }
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      if (!variables.silent) {
        toast.success('Workflow updated')
      }
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (isWorkflowLockConflict(ae)) return
      if (ae.status === 413) {
        toast.error(ae.message || WORKFLOW_PAYLOAD_TOO_LARGE_MESSAGE)
        return
      }
      toast.error(ae?.message || 'Failed to update workflow')
    },
  })
}

/** Suggest a workflow description from the current node graph */
export function useGenerateWorkflowDescription() {
  return useMutation({
    mutationFn: ({
      workflowId,
      graph_definition,
    }: {
      workflowId: string
      graph_definition?: { nodes: WorkflowNode[]; edges: WorkflowEdge[] }
    }) => aiworkflowsApi.generateWorkflowDescription(workflowId, graph_definition ? { graph_definition } : undefined),
  })
}

function invalidateWorkflowTriggerQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  workflowId: string
) {
  queryClient.invalidateQueries({ queryKey: queryKeys.workflows.schedule(workflowId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.workflows.triggers(workflowId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.workflows.controls(workflowId) })
  queryClient.invalidateQueries({ queryKey: queryKeys.workflows.versions(workflowId) })
}

/** Delete a workflow */
export function useDeleteWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => aiworkflowsApi.deleteWorkflow(id),
    onSuccess: (data, id) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      invalidateWorkflowTriggerQueries(queryClient, id)
      const permanent = data?.message?.toLowerCase().includes('permanently')
      toast.success(permanent ? 'Workflow removed permanently' : 'Workflow archived')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 409) return
      toast.error(ae?.message || 'Failed to delete workflow')
    },
  })
}

/**
 * Detect the 409 "name already exists" conflict returned by the workflow
 * unarchive endpoint, which requires the user to supply a new name.
 */
export function isWorkflowUnarchiveNameConflict(err: unknown): boolean {
  if (!(err instanceof ApiError) || err.status !== 409) return false
  const data = err.data as { error?: Record<string, unknown> } | undefined
  const root = data?.error ?? {}
  return (
    root.requires_rename === true ||
    root.conflict_code === 'WORKFLOW_NAME_EXISTS' ||
    /Provide a new name/i.test(err.message)
  )
}

/** Restore an archived (soft-deleted) workflow to draft */
export function useUnarchiveWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, name }: { id: string; name?: string }) =>
      aiworkflowsApi.unarchiveWorkflow(id, name ? { name } : undefined),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      invalidateWorkflowTriggerQueries(queryClient, id)
      toast.success('Workflow restored to draft')
    },
    onError: (error: Error) => {
      // Name conflicts are handled by the caller (rename dialog); don't toast.
      if (isWorkflowUnarchiveNameConflict(error)) return
      toast.error(error?.message || 'Failed to restore workflow')
    },
  })
}

/** Clear all incremental node markers for a workflow (File Reader, Database Reader, etc.) */
export function useClearWorkflowMarkers() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (workflowId: string) => aiworkflowsApi.clearAllWorkflowMarkers(workflowId),
    onSuccess: (data, workflowId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(workflowId) })
      const n = data?.deleted_count ?? 0
      toast.success(
        n === 0
          ? 'No incremental state was stored for this workflow.'
          : `Cleared incremental state for ${n} node${n === 1 ? '' : 's'}.`
      )
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to clear state.')
        return
      }
      // AbortSignal.timeout fires a DOMException (TimeoutError); network aborts fire
      // AbortError. Either means the request never came back within the budget.
      if (error?.name === 'TimeoutError' || error?.name === 'AbortError') {
        toast.error('Clearing state timed out — the backend did not respond. Please try again.')
        return
      }
      toast.error(error?.message || 'Failed to clear incremental state')
    },
  })
}

/** Publish a workflow (create immutable version) */
export function usePublishWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, commitMessage }: { id: string; commitMessage?: string }) =>
      aiworkflowsApi.publishWorkflow(id, commitMessage ? { commit_message: commitMessage } : undefined),
    onSuccess: (_data, { id }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(id) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      invalidateWorkflowTriggerQueries(queryClient, id)
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.versions(id) })
      toast.success('Workflow published')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to publish the workflow')
        return
      }
      toast.error(error?.message || 'Failed to publish workflow')
    },
  })
}

/** Register a published workflow in the tenant workflow registry */
export function useRegisterWorkflowToRegistry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (workflowId: string) => aiworkflowsApi.registerWorkflowToRegistry(workflowId),
    onSuccess: (data, workflowId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      queryClient.invalidateQueries({ queryKey: ['workflow-registry'] })
      const name = (data?.name && String(data.name).trim()) || 'This workflow'
      toast.success(
        `Registration request submitted for "${name}". A tenant admin must approve it before it appears in the Workflow Registry.`,
      )
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to register the workflow to registry')
        return
      }
      toast.error(error?.message || 'Failed to register workflow')
    },
  })
}

/** Remove a workflow from the registry (archives registry entry; workflow stays published) */
export function useUnregisterWorkflowFromRegistry() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (workflowId: string) => aiworkflowsApi.unregisterWorkflowFromRegistry(workflowId),
    onSuccess: (_data, workflowId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      queryClient.invalidateQueries({ queryKey: ['workflow-registry'] })
      toast.success('Workflow removed from registry')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 409) return
      toast.error(error?.message || 'Failed to remove workflow from registry')
    },
  })
}

/** List workflow versions (for version switcher) */
export function useWorkflowVersions(workflowId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workflows.versions(workflowId!),
    queryFn: () => aiworkflowsApi.listWorkflowVersions(workflowId!),
    enabled: !!workflowId,
  })
}

/** Get a specific version's graph (for viewing history) */
export function useWorkflowVersion(workflowId: string | undefined, versionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workflows.versionDetail(workflowId!, versionId!),
    queryFn: () => aiworkflowsApi.getWorkflowVersion(workflowId!, versionId!),
    enabled: !!workflowId && !!versionId,
    staleTime: 60_000,
  })
}

/** Create new draft version (when user clicks "New Version" on published workflow) */
export function useCreateNewVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (workflowId: string) => aiworkflowsApi.createNewVersion(workflowId),
    onSuccess: async (data, workflowId) => {
      applyNewDraftVersionToListCaches(queryClient, workflowId, data)
      await invalidateWorkflowVersionQueries(queryClient, workflowId)
      toast.success('New draft version created')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to create new version')
    },
  })
}

/** Fork a new draft from a specific past version */
export function useForkWorkflowVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ workflowId, versionId }: { workflowId: string; versionId: string }) =>
      aiworkflowsApi.forkWorkflowVersion(workflowId, versionId),
    onSuccess: async (data, { workflowId }) => {
      applyNewDraftVersionToListCaches(queryClient, workflowId, data)
      await invalidateWorkflowVersionQueries(queryClient, workflowId)
      toast.success('Draft forked from selected version')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to fork version')
    },
  })
}

export function useArchiveWorkflowVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ workflowId, versionId }: { workflowId: string; versionId: string }) =>
      aiworkflowsApi.archiveWorkflowVersion(workflowId, versionId),
    onSuccess: (_data, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.versions(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.archivedVersions() })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.schedule(workflowId) })
      toast.success('Version archived')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to archive version')
    },
  })
}

/** Point the editor at an existing draft without creating a version. */
export function useSwitchToDraftVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ workflowId, versionId }: { workflowId: string; versionId: string }) =>
      aiworkflowsApi.switchToDraftVersion(workflowId, versionId),
    onSuccess: async (data, { workflowId }) => {
      // Do not reuse applyNewDraftVersionToListCaches — that helper inserts a
      // version row, and this version already exists.
      await invalidateWorkflowVersionQueries(queryClient, workflowId)
      toast.success(data.message || 'Now editing this draft')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to switch to draft')
    },
  })
}

export function useRestoreWorkflowVersion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ workflowId, versionId }: { workflowId: string; versionId: string }) =>
      aiworkflowsApi.restoreWorkflowVersion(workflowId, versionId),
    onSuccess: (_data, { workflowId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.versions(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.archivedVersions() })
      toast.success('Version restored as draft')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to restore version')
    },
  })
}

export function useArchivedWorkflowVersions(
  params?: { search?: string; page?: number; page_size?: number },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.workflows.archivedVersions(params),
    queryFn: () => aiworkflowsApi.listArchivedWorkflowVersions(params),
    enabled: options?.enabled ?? true,
    staleTime: 15_000,
  })
}

/** Fork workflow: new workflow (new card) with same name, new version. Returns the created workflow. */
export function useCloneWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      workflowId,
      projectId,
      versionId,
    }: {
      workflowId: string
      /** Target project for the fork; omit or same as source to fork in the current project without extra body fields. */
      projectId?: string
      /** Published version whose canvas should be applied to the forked workflow card. */
      versionId?: string
    }) => {
      const versionDetail = versionId
        ? await aiworkflowsApi.getWorkflowVersion(workflowId, versionId)
        : undefined

      let cloned = await aiworkflowsApi.cloneWorkflow(
        workflowId,
        projectId ? { project_id: projectId } : undefined,
      )

      if (versionDetail?.graph_definition != null && versionId) {
        if (cloned.lock_version == null) {
          throw new Error('lock_version is required. Refresh the workflow and retry.')
        }
        cloned = await aiworkflowsApi.updateWorkflow(cloned.id, {
          lock_version: cloned.lock_version,
          graph_definition: versionDetail.graph_definition,
          ...(versionDetail.trigger_config != null
            ? { trigger_config: versionDetail.trigger_config }
            : {}),
        })
      }

      return cloned
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKeys.workflows.detail(data.id), data)
      queryClient.setQueriesData<{ items: WorkflowReadResponse[]; total: number }>(
        { queryKey: queryKeys.workflows.lists() },
        (old) => {
          if (!old?.items) return old
          return { ...old, items: [data, ...old.items], total: old.total + 1 }
        }
      )
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(data.id) })
      toast.success('Workflow forked; opening new version')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to fork the workflow')
        return
      }
      toast.error(error?.message || 'Failed to fork workflow')
    },
  })
}

// =============================================================================
// EXECUTION QUERIES & MUTATIONS
// =============================================================================

/** Fetch executions for a workflow (supports offset/limit for server-side pagination) */
export function useWorkflowExecutions(
  workflowId: string | undefined,
  params?: {
    offset?: number
    limit?: number
    search?: string
    deployment_environment?: string
    deployment_id?: string
    trigger_source?: string
  }
) {
  return useQuery({
    queryKey: [...queryKeys.workflows.executions(workflowId!), params ?? {}],
    queryFn: () => aiworkflowsApi.listExecutions(workflowId!, params).then((res) => res ?? null),
    enabled: !!workflowId,
    staleTime: 10_000,
    refetchOnMount: 'always', // Always refetch on entering the Runs tab so a freshly-started run is visible
    // Poll so scheduled runs appear within 15s; stop when project/workflow is gone (404).
    refetchInterval: executionsRefetchInterval,
  })
}

/** Fetch all executions across workflows for the tenant (Runs view), latest first */
export function useAllExecutions(params?: {
  project_id?: string
  offset?: number
  limit?: number
  search?: string
  status?: string
  trigger_source?: string
  started_by?: string
  /** Client-only scope key to force refresh when catalog scope toggles. */
  catalog_scope?: 'active' | 'archived'
}, options?: { enabled?: boolean }) {
  const apiParams = params
    ? {
        project_id: params.project_id,
        offset: params.offset,
        limit: params.limit,
        search: params.search,
        status: params.status,
        trigger_source: params.trigger_source,
        started_by: params.started_by,
      }
    : undefined
  return useQuery({
    queryKey: queryKeys.workflows.allExecutions(params),
    queryFn: () => aiworkflowsApi.listAllExecutions(apiParams),
    enabled: options?.enabled ?? true,
    staleTime: 10_000,
    // Stop when filtered project (or similar) returns terminal 404 — keep retrying 503.
    refetchInterval: executionsRefetchInterval,
    refetchOnMount: true,
  })
}

/** Platform Admin only: pending/running executions across the tenant (Active Runs tab). */
export function useActiveExecutions(
  params?: { offset?: number; limit?: number; search?: string },
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: queryKeys.workflows.activeExecutions(params),
    queryFn: () => aiworkflowsApi.listActiveExecutions(params),
    enabled: options?.enabled ?? true,
    staleTime: 5_000,
    refetchInterval: 5_000,
    refetchOnMount: true,
    placeholderData: keepPreviousData,
  })
}

/** Platform Admin only: terminate one or more in-progress executions from Active Runs. */
export function useTerminateActiveExecutions() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (executionIds: string[]) =>
      aiworkflowsApi.terminateExecutionsBulk(executionIds),
    onMutate: (executionIds) => {
      for (const id of executionIds) {
        markExecutionStoppedByUser(id)
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.workflows.all, 'active-executions'],
      })
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.workflows.all, 'all-executions'],
      })
      for (const r of data.results) {
        if (r.ok) {
          void queryClient.refetchQueries({
            queryKey: queryKeys.workflows.executionDetail(r.execution_id),
          })
        }
      }
      if (data.terminated > 0 && data.failed === 0) {
        toast.success(
          data.terminated === 1
            ? 'Run terminated'
            : `${data.terminated} runs terminated`,
        )
      } else if (data.terminated > 0 && data.failed > 0) {
        toast.warning(
          `Terminated ${data.terminated}, failed ${data.failed}`,
        )
      } else {
        toast.error(data.results[0]?.error || 'Failed to terminate runs')
      }
    },
    onError: (error: { message?: string }) => {
      toast.error(error?.message || 'Failed to terminate runs')
    },
  })
}

/** Fetch a single execution */
export function useWorkflowExecution(executionId: string | undefined) {
  const scope = useDeploymentRunScope()
  const environment = scope?.environment
  return useQuery({
    queryKey: environment
      ? [...queryKeys.workflows.executionDetail(executionId!), environment]
      : queryKeys.workflows.executionDetail(executionId!),
    queryFn: () => aiworkflowsApi.getExecution(executionId!, { deploymentEnvironment: environment }),
    enabled: !!executionId,
    staleTime: 5_000,
    refetchInterval: (query) => {
      if (query.state.status === 'error') return false
      const data = query.state.data as ExecutionReadResponse | undefined
      if (!data) return 1_500
      if (data.status === 'running' || data.status === 'pending') return 2_000
      return false
    },
  })
}

interface ExecutionNodesPollQuery {
  state: { dataUpdateCount: number; status?: string }
}

function executionNodesRefetchInterval(
  query: ExecutionNodesPollQuery,
  interval:
    | number
    | false
    | ((query: { state: { dataUpdateCount: number } }) => number | false)
    | undefined,
): number | false {
  if (query.state.status === 'error' || interval == null || interval === false) {
    return false
  }
  if (typeof interval === 'function') return interval(query)
  return interval
}

/** Fetch node-level execution details. Pass refetchInterval when execution is running for live updates. */
export function useExecutionNodes(
  executionId: string | undefined,
  options?: {
    refetchInterval?:
      | number
      | false
      | ((query: { state: { dataUpdateCount: number } }) => number | false)
  }
) {
  const scopeEnvironment = useDeploymentRunScope()?.environment
  return useQuery({
    queryKey: scopeEnvironment
      ? [...queryKeys.workflows.executionNodes(executionId!), scopeEnvironment]
      : queryKeys.workflows.executionNodes(executionId!),
    queryFn: () => aiworkflowsApi.getExecutionNodes(executionId!, { deploymentEnvironment: scopeEnvironment }),
    enabled: !!executionId,
    staleTime: 5_000,
    refetchInterval: (query) => executionNodesRefetchInterval(query, options?.refetchInterval),
  })
}

/** Resolved full input/output payloads for the node execution viewer. */
export function useNodeExecutionDetails(
  executionId: string | undefined,
  nodeId: string | undefined,
  options?: { enabled?: boolean; unmask?: boolean }
) {
  const unmask = Boolean(options?.unmask)
  const environment = useDeploymentRunScope()?.environment
  return useQuery({
    queryKey: [...queryKeys.workflows.executionNodeDetails(executionId!, nodeId!), unmask ? 'unmask' : 'masked', environment ?? ''],
    queryFn: () => aiworkflowsApi.getNodeExecutionDetails(executionId!, nodeId!, { unmask, deploymentEnvironment: environment }),
    enabled: (options?.enabled ?? true) && !!executionId && !!nodeId,
    staleTime: 30_000,
  })
}

/** Trigger a workflow execution */
export function useExecuteWorkflow() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      parameters,
      deploymentId,
      deploymentEnvironment,
      triggerSource,
      previewMode,
      previewLimit,
      runDraft,
      targetNodeIds,
      includeUpstream,
      nodeTestInputs,
      forceTestInputNodeIds,
      versionNumber,
    }: {
      workflowId: string
      parameters?: Record<string, unknown>
      deploymentId?: string
      deploymentEnvironment?: string
      triggerSource?: string
      previewMode?: boolean
      previewLimit?: number
      runDraft?: boolean
      targetNodeIds?: string[]
      includeUpstream?: boolean
      nodeTestInputs?: Record<string, Record<string, unknown>>
      forceTestInputNodeIds?: string[]
      /** Pin execute to this version number (published or draft). */
      versionNumber?: number
    }) => aiworkflowsApi.triggerExecution(workflowId, parameters, {
      deployment_id: deploymentId,
      deployment_environment: deploymentEnvironment,
      trigger_source: triggerSource,
      preview_mode: previewMode,
      preview_limit: previewLimit,
      run_draft: runDraft,
      target_node_ids: targetNodeIds,
      include_upstream: includeUpstream,
      node_test_inputs: nodeTestInputs,
      force_test_input_node_ids: forceTestInputNodeIds,
      versionNumber,
    }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.executions(variables.workflowId),
      })
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.workflows.all, 'all-executions'],
      })
      toast.success(
        variables.previewMode
          ? 'Preview run started'
          : variables.targetNodeIds && variables.targetNodeIds.length > 0
            ? `Running ${variables.targetNodeIds.length} selected node${variables.targetNodeIds.length > 1 ? 's' : ''}`
            : 'Workflow execution started'
      )
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to execute the workflow')
        return
      }
      if (ae.status === 413) {
        toast.error(ae.message || WORKFLOW_PAYLOAD_TOO_LARGE_MESSAGE)
        return
      }
      toast.error(error?.message || 'Failed to start workflow execution')
    },
  })
}

function isCancelAlreadyTerminalError(message: string | undefined): boolean {
  if (!message) return false
  return /cannot cancel execution in .* state/i.test(message)
}

/** Cancel a running execution */
export function useCancelExecution() {
  const queryClient = useQueryClient()
  const environment = useDeploymentRunScope()?.environment

  const syncExecutionAfterCancel = (executionId: string) => {
    void queryClient.refetchQueries({
      queryKey: queryKeys.workflows.executionDetail(executionId),
    })
    void queryClient.refetchQueries({
      queryKey: queryKeys.workflows.executionNodes(executionId),
    })
    queryClient.invalidateQueries({
      queryKey: [...queryKeys.workflows.all, 'all-executions'],
    })
  }

  return useMutation({
    mutationFn: (executionId: string) => aiworkflowsApi.cancelExecution(executionId, { deploymentEnvironment: environment }),
    onMutate: (executionId) => {
      markExecutionStoppedByUser(executionId)
    },
    onSuccess: (_data, executionId) => {
      // Timestamps on ExecutionReadResponse / NodeExecutionReadResponse are
      // recorded by the backend in microseconds (see WorkflowRunsView duration
      // calc: completed_at/1_000_000 → seconds). Mirror that unit here.
      const nowMicros = Date.now() * 1000
      // Optimistically mark the execution as cancelled so any mounted view
      // (Canvas or Runs) reflects the new status immediately, even before the
      // server-side refetch lands.
      queryClient.setQueryData<ExecutionReadResponse | undefined>(
        queryKeys.workflows.executionDetail(executionId),
        (prev) =>
          prev
            ? { ...prev, status: 'cancelled', completed_at: prev.completed_at ?? nowMicros }
            : prev
      )
      // Mark any still-running/pending nodes as cancelled in the cached node list.
      // Cache shape matches getExecutionNodes: { items, _privacy? }, not a flat array.
      queryClient.setQueryData<{ items: NodeExecutionReadResponse[]; _privacy?: unknown } | undefined>(
        queryKeys.workflows.executionNodes(executionId),
        (prev) =>
          prev
            ? {
                ...prev,
                items: prev.items.map((n) =>
                  n.status === 'running' || n.status === 'pending'
                    ? { ...n, status: 'cancelled', completed_at: n.completed_at ?? nowMicros }
                    : n
                ),
              }
            : prev
      )
      syncExecutionAfterCancel(executionId)
      toast.warning(WORKFLOW_STOPPED_TOAST_MESSAGE)
    },
    onError: (error: Error, executionId) => {
      if (isCancelAlreadyTerminalError(error?.message)) {
        // Run finished while stop was in flight — update cache with actual terminal status immediately
        const match = error?.message?.match(/cannot cancel execution in (?:ExecutionStatus\.)?(\w+) state/i)
        const actualStatus = match ? match[1].toLowerCase() : undefined
        if (actualStatus) {
          queryClient.setQueryData<ExecutionReadResponse | undefined>(
            queryKeys.workflows.executionDetail(executionId),
            (prev) => (prev ? { ...prev, status: actualStatus as ExecutionReadResponse['status'] } : prev)
          )
        }

        syncExecutionAfterCancel(executionId)

        // Show stop feedback only if the actual status is not success, failed, or timed_out
        const isNotStopped = actualStatus === 'success' || actualStatus === 'failed' || actualStatus === 'timed_out'
        if (!isNotStopped) {
          toast.warning(WORKFLOW_STOPPED_TOAST_MESSAGE)
        } else if (actualStatus === 'success') {
          toast.success('Workflow execution finished successfully')
        }
        return
      }
      toast.error(error?.message || 'Failed to cancel execution')
    },
  })
}

/** Pause a pending/running execution at the next node boundary. */
export function usePauseExecution() {
  const queryClient = useQueryClient()
  const environment = useDeploymentRunScope()?.environment

  return useMutation({
    mutationFn: (executionId: string) => aiworkflowsApi.pauseExecution(executionId, { deploymentEnvironment: environment }),
    onSuccess: (_data, executionId) => {
      void queryClient.refetchQueries({
        queryKey: queryKeys.workflows.executionDetail(executionId),
      })
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.workflows.all, 'all-executions'],
      })
      toast.success('Pause requested — takes effect after the current node finishes')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You do not have permission to pause this execution')
        return
      }
      toast.error(error?.message || 'Failed to pause execution')
    },
  })
}

/** Continue a paused execution on the same orchestrator workflow. */
export function useResumePausedExecution() {
  const queryClient = useQueryClient()
  const environment = useDeploymentRunScope()?.environment

  return useMutation({
    mutationFn: (executionId: string) =>
      aiworkflowsApi.resumePausedExecution(executionId, { deploymentEnvironment: environment }),
    onSuccess: (_data, executionId) => {
      queryClient.setQueryData<ExecutionReadResponse | undefined>(
        queryKeys.workflows.executionDetail(executionId),
        (prev) => (prev ? { ...prev, status: 'running' } : prev),
      )
      void queryClient.refetchQueries({
        queryKey: queryKeys.workflows.executionDetail(executionId),
      })
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.workflows.all, 'all-executions'],
      })
      toast.success('Execution resumed')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You do not have permission to resume this execution')
        return
      }
      toast.error(error?.message || 'Failed to resume execution')
    },
  })
}

/** Preview which nodes would be re-run vs restored for a resume */
export function useResumeExecutionPreview(
  executionId: string | undefined,
  options?: {
    enabled?: boolean
    strategy?: ResumeStrategy
    targetNodeIds?: string[]
    rerunDependents?: boolean
  }
) {
  const targetCsv =
    options?.targetNodeIds?.length ? options.targetNodeIds.join(',') : undefined
  const environment = useDeploymentRunScope()?.environment
  return useQuery({
    queryKey: [
      ...queryKeys.workflows.all,
      'resume-preview',
      executionId,
      options?.strategy ?? 'FROM_FAILURE',
      targetCsv ?? '',
      options?.rerunDependents ?? true,
      environment ?? '',
    ],
    queryFn: () =>
      aiworkflowsApi.getResumePreview(executionId!, {
        strategy: options?.strategy,
        target_node_ids: targetCsv,
        rerun_dependents: options?.rerunDependents,
        deploymentEnvironment: environment,
      }),
    enabled: !!executionId && (options?.enabled ?? true),
    staleTime: 0,
    retry: false,
  })
}

/** Resume a failed/cancelled/timed-out execution from the failure point */
export function useResumeExecution() {
  const queryClient = useQueryClient()
  const environment = useDeploymentRunScope()?.environment

  return useMutation({
    mutationFn: ({
      executionId,
      body,
    }: {
      executionId: string
      workflowId: string
      body?: ResumeExecutionRequest
    }) => aiworkflowsApi.resumeExecution(executionId, body ?? {}, { deploymentEnvironment: environment }),
    onSuccess: (data, variables) => {
      queryClient.refetchQueries({
        queryKey: queryKeys.workflows.executions(variables.workflowId),
      })
      queryClient.invalidateQueries({
        queryKey: [...queryKeys.workflows.all, 'all-executions'],
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.executionDetail(data.execution_id),
      })
      toast.success(
        `Resume started — ${data.nodes_restored} node(s) restored, ${data.nodes_to_rerun.length} to re-run`
      )
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You do not have permission to resume this execution')
        return
      }
      toast.error(error?.message || 'Failed to resume execution')
    },
  })
}

// =============================================================================
// SCHEDULE QUERIES & MUTATIONS
// =============================================================================

/** Fetch schedule for a workflow */
export function useWorkflowSchedule(workflowId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workflows.schedule(workflowId!),
    queryFn: () => aiworkflowsApi.getSchedule(workflowId!),
    enabled: !!workflowId,
    staleTime: 60_000,
    retry: false, // Don't retry 404 when no schedule exists
  })
}

/** Create a schedule */
export function useCreateSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      data,
    }: {
      workflowId: string
      data: ScheduleCreatePayload
    }) => aiworkflowsApi.createSchedule(workflowId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.schedule(variables.workflowId),
      })
      toast.success('Schedule created')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to create schedule')
    },
  })
}

/** Update a schedule */
export function useUpdateSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      data,
    }: {
      workflowId: string
      data: Partial<ScheduleCreatePayload>
    }) => aiworkflowsApi.updateSchedule(workflowId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.schedule(variables.workflowId),
      })
      toast.success('Schedule updated')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to update schedule')
    },
  })
}

/** Delete a schedule */
export function useDeleteSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (workflowId: string) => aiworkflowsApi.deleteSchedule(workflowId),
    onSuccess: (_data, workflowId) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.schedule(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.detail(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      toast.success('Schedule deleted')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to delete schedule')
    },
  })
}

/** Pause a schedule */
export function usePauseSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: string | { workflowId: string; versionId?: string }) => {
      if (typeof args === 'string') return aiworkflowsApi.pauseSchedule(args)
      return aiworkflowsApi.pauseSchedule(args.workflowId, args.versionId)
    },
    onSuccess: (_data, args) => {
      const workflowId = typeof args === 'string' ? args : args.workflowId
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.schedule(workflowId),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.controls(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.versions(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      toast.success('Schedule paused')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to pause schedule')
    },
  })
}

/** Resume a schedule */
export function useResumeSchedule() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (args: string | { workflowId: string; versionId?: string }) => {
      if (typeof args === 'string') return aiworkflowsApi.resumeSchedule(args)
      return aiworkflowsApi.resumeSchedule(args.workflowId, args.versionId)
    },
    onSuccess: (_data, args) => {
      const workflowId = typeof args === 'string' ? args : args.workflowId
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.schedule(workflowId),
      })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.controls(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.versions(workflowId) })
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      toast.success('Schedule resumed')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to resume schedule')
    },
  })
}

// =============================================================================
// WORKFLOW CONTROLS (unified pause/resume for triggers + schedule)
// =============================================================================

/** Fetch unified runtime controls for a published workflow */
export function useWorkflowControls(
  workflowId: string | undefined,
  enabled = true,
  deploymentId?: string,
) {
  return useQuery({
    queryKey: queryKeys.workflows.controls(workflowId!, deploymentId),
    queryFn: () => aiworkflowsApi.getWorkflowControls(workflowId!, deploymentId),
    enabled: !!workflowId && enabled,
    staleTime: 15_000,
  })
}

/** Pause or resume a single control item (trigger or schedule) */
export function useUpdateWorkflowControlStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      itemId,
      data,
      deploymentId,
    }: {
      workflowId: string
      itemId: string
      data: WorkflowControlStatusUpdatePayload
      deploymentId?: string
    }) =>
      aiworkflowsApi.updateWorkflowControlStatus(workflowId, itemId, data, deploymentId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        queryKeys.workflows.controls(variables.workflowId, variables.deploymentId),
        data,
      )
      invalidateWorkflowTriggerQueries(queryClient, variables.workflowId)
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      toast.success(variables.data.status === 'paused' ? 'Trigger paused' : 'Trigger resumed')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to update trigger')
    },
  })
}

/** Pause all runtime triggers + schedule */
export function usePauseAllWorkflowControls() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      deploymentId,
    }: { workflowId: string; deploymentId?: string }) =>
      aiworkflowsApi.pauseAllWorkflowControls(workflowId, deploymentId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKeys.workflows.controls(variables.workflowId, variables.deploymentId), {
        workflow_id: data.workflow_id,
        master_status: data.master_status,
        items: data.items,
      })
      invalidateWorkflowTriggerQueries(queryClient, variables.workflowId)
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      toast.success(
        data.updated === 0
          ? 'All triggers already paused'
          : data.updated === 1
            ? 'Paused 1 trigger'
            : `Paused ${data.updated} triggers`
      )
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to pause triggers')
    },
  })
}

/** Resume all runtime triggers + schedule */
export function useResumeAllWorkflowControls() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      deploymentId,
    }: { workflowId: string; deploymentId?: string }) =>
      aiworkflowsApi.resumeAllWorkflowControls(workflowId, deploymentId),
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKeys.workflows.controls(variables.workflowId, variables.deploymentId), {
        workflow_id: data.workflow_id,
        master_status: data.master_status,
        items: data.items,
      })
      invalidateWorkflowTriggerQueries(queryClient, variables.workflowId)
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      toast.success(
        data.updated === 0
          ? 'All triggers already active'
          : data.updated === 1
            ? 'Resumed 1 trigger'
            : `Resumed ${data.updated} triggers`
      )
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to resume triggers')
    },
  })
}

// =============================================================================
// TRIGGER QUERIES & MUTATIONS (Webhook + Storage)
// =============================================================================

/** Fetch triggers for a workflow */
export function useWorkflowTriggers(workflowId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workflows.triggers(workflowId!),
    queryFn: () => aiworkflowsApi.listTriggers(workflowId!),
    enabled: !!workflowId,
    staleTime: 30_000,
  })
}

/** Create or update a trigger for a workflow (requires published workflow) */
export function useUpsertWorkflowTrigger() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      data,
    }: {
      workflowId: string
      data: TriggerUpsertPayload
    }) => aiworkflowsApi.upsertTrigger(workflowId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.triggers(variables.workflowId),
      })
      toast.success('Trigger saved')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to save trigger')
    },
  })
}

/** Update trigger status (active/paused/disabled) */
export function useUpdateWorkflowTriggerStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      triggerId,
      data,
    }: {
      workflowId: string
      triggerId: string
      data: TriggerStatusUpdatePayload
    }) => aiworkflowsApi.updateTriggerStatus(workflowId, triggerId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.triggers(variables.workflowId),
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.controls(variables.workflowId),
      })
      toast.success('Trigger updated')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to update trigger')
    },
  })
}

/** Fetch webhook trigger tokens */
export function useWorkflowTriggerTokens(
  workflowId: string | undefined,
  triggerId: string | undefined
) {
  return useQuery({
    queryKey: queryKeys.workflows.triggerTokens(workflowId!, triggerId!),
    queryFn: () => aiworkflowsApi.listTriggerTokens(workflowId!, triggerId!),
    enabled: !!workflowId && !!triggerId,
    staleTime: 30_000,
  })
}

/** Create a new webhook trigger token */
export function useCreateWorkflowTriggerToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      triggerId,
      data,
    }: {
      workflowId: string
      triggerId: string
      data?: TriggerTokenCreatePayload
    }) => aiworkflowsApi.createTriggerToken(workflowId, triggerId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.triggerTokens(variables.workflowId, variables.triggerId),
      })
      toast.success('Webhook token created')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to create a token')
        return
      }
      toast.error(error?.message || 'Failed to create webhook token')
    },
  })
}

/** Update an existing webhook trigger token */
export function useUpdateWorkflowTriggerToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      triggerId,
      tokenId,
      data,
    }: {
      workflowId: string
      triggerId: string
      tokenId: string
      data: TriggerTokenUpdatePayload
    }) => aiworkflowsApi.updateTriggerToken(workflowId, triggerId, tokenId, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.triggerTokens(variables.workflowId, variables.triggerId),
      })
      toast.success('Webhook token updated')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have access to update the webhook token')
        return
      }
      toast.error(error?.message || 'Failed to update webhook token')
    },
  })
}

/** Revoke an existing webhook trigger token */
export function useRevokeWorkflowTriggerToken() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      triggerId,
      tokenId,
    }: {
      workflowId: string
      triggerId: string
      tokenId: string
    }) => aiworkflowsApi.revokeTriggerToken(workflowId, triggerId, tokenId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.workflows.triggerTokens(variables.workflowId, variables.triggerId),
      })
      toast.success('Webhook token revoked')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have access to revoke the webhook')
        return
      }
      toast.error(error?.message || 'Failed to revoke webhook token')
    },
  })
}

// =============================================================================
// WORKFLOW TEMPLATES (Solution Templates)
// =============================================================================

/** Fetch workflow templates list */
export function useTemplates(params?: {
  include_archived?: boolean
  search?: string
  offset?: number
  limit?: number
  created_by?: string | string[]
}) {
  return useQuery({
    queryKey: queryKeys.workflows.templates({
      include_archived: params?.include_archived,
      search: params?.search,
      created_by: params?.created_by,
    }),
    queryFn: () => aiworkflowsApi.listTemplates(params),
    staleTime: 30_000,
  })
}

/** Fetch workflow templates list with infinite scroll pagination */
export function useTemplatesInfiniteQuery(
  params?: {
    include_archived?: boolean
    search?: string
    created_by?: string | string[]
  },
  options?: {
    pageSize?: number
    enabled?: boolean
    staleTime?: number
  }
) {
  const pageSize = options?.pageSize ?? 50
  const queryKey = [
    ...queryKeys.workflows.templates(params),
    'infinite',
    { pageSize },
  ] as const

  const result = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam = 0 }) =>
      aiworkflowsApi.listTemplates({
        ...params,
        offset: pageParam,
        limit: pageSize,
      }),
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length
      if (nextOffset >= lastPage.total) return undefined
      return nextOffset
    },
    initialPageParam: 0,
    enabled: options?.enabled ?? true,
    staleTime: options?.staleTime ?? 30_000,
  })

  const items = useMemo(() => result.data?.pages.flatMap((page) => page.items) ?? [], [result.data?.pages])
  const total = result.data?.pages[0]?.total ?? 0

  return {
    ...result,
    items,
    total,
    isLoadingMore: result.isFetchingNextPage,
    hasMore: result.hasNextPage ?? false,
    loadMore: result.fetchNextPage,
  }
}

/** Fetch a single template by ID */
export function useTemplate(templateId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.workflows.templateDetail(templateId!),
    queryFn: () => aiworkflowsApi.getTemplate(templateId!),
    enabled: !!templateId,
    staleTime: 30_000,
  })
}

/** Publish current workflow as a Solution Template */
export function usePublishTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      workflowId,
      data,
    }: {
      workflowId: string
      data: WorkflowTemplateCreatePayload
    }) => aiworkflowsApi.publishTemplate(workflowId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.templates() })
      toast.success('Template published')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to publish the template')
        return
      }
      toast.error(error.message || 'Failed to publish template')
    },
  })
}

/** Archive a template (admin) */
export function useArchiveTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (templateId: string) => aiworkflowsApi.archiveTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.templates() })
      toast.success('Template archived')
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to archive templates.')
        return
      }
      toast.error(error?.message || 'Failed to archive template')
    },
  })
}

/** Unarchive a template (admin) */
export function useUnarchiveTemplate() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (templateId: string) => aiworkflowsApi.unarchiveTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.workflows.templates() })
      toast.success('Template unarchived')
    },
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to unarchive template')
    },
  })
}

// =============================================================================
// NODE REGISTRY
// =============================================================================

/** Fetch node registry (types + beta status) */
export function useNodeRegistry() {
  return useQuery({
    queryKey: queryKeys.workflows.nodeRegistry(),
    queryFn: () => aiworkflowsApi.getNodeRegistry(),
    staleTime: 5 * 60_000, // 5 minutes
  })
}

// =============================================================================
// WORKFLOW DEPENDENCIES (for export modal)
// =============================================================================

/** Fetch workflow dependencies for export modal (enriched from AIWorkflows API). */
export function useWorkflowDependencies(workflowId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.workflows.dependencies(workflowId!),
    queryFn: () => aiworkflowsApi.getWorkflowDependencies(workflowId!),
    enabled: !!workflowId && enabled,
    staleTime: 60_000,
  })
}

// =============================================================================
// EXPORT / IMPORT
// =============================================================================

/** Export a single workflow as YAML and trigger download */
export function useExportWorkflow() {
  return useMutation({
    mutationFn: async ({ workflowId, workflowName }: { workflowId: string; workflowName: string }) => {
      const content = await aiworkflowsApi.exportWorkflow(workflowId)
      const filename = generateExportFilename('workflow', workflowName, 'yaml')
      downloadAsFile(content, filename, 'text/yaml')
      return { content, filename }
    },
    onSuccess: (_, { workflowName }) => {
      toast.success(`Exported "${workflowName}" successfully`)
    },
    onError: (error: Error) => {
      toast.error(`Export failed: ${error.message}`)
    },
  })
}

/** PMA-gated BPMN availability for a saved workflow (hide button when false). */
export function useBpmnExportAvailable(workflowId: string, enabled = true) {
  return useQuery({
    queryKey: ['workflows', 'bpmn-available', workflowId],
    queryFn: () => aiworkflowsApi.bpmnExportAvailable(workflowId),
    enabled: Boolean(workflowId) && enabled,
    staleTime: 30_000,
  })
}

/** Download plain BPMN XML when PMA IR was saved with the workflow. */
export function useExportWorkflowBpmn() {
  return useMutation({
    mutationFn: async ({ workflowId, workflowName }: { workflowId: string; workflowName: string }) => {
      const { blob, filename } = await aiworkflowsApi.exportWorkflowBpmn(workflowId)
      const url = URL.createObjectURL(blob)
      try {
        const a = document.createElement('a')
        a.href = url
        a.download = filename || `${workflowName.replace(/\s+/g, '_') || 'workflow'}.bpmn`
        document.body.appendChild(a)
        a.click()
        a.remove()
      } finally {
        URL.revokeObjectURL(url)
      }
      return { filename }
    },
    onSuccess: (_, { workflowName }) => {
      toast.success(`Exported BPMN for "${workflowName}"`)
    },
    onError: (error: Error) => {
      toast.error(`BPMN export failed: ${error.message}`)
    },
  })
}

export type WorkflowDependencyEntityType =
  | 'model'
  | 'connector'
  | 'iam'
  | 'agent'
  | 'prompt'
  | 'skill'
  | 'tool'
  | 'knowledge'

const AGENT_DEP_CATEGORIES = ['prompts', 'skills', 'tools', 'knowledge'] as const
const WORKFLOW_DEP_CATEGORIES = ['models', 'connectors', 'iam', 'tools'] as const

function categoryHasSelection(
  items: Array<{ id: string }> | undefined,
  deselectedIds: Set<string>
): boolean {
  if (!items?.length) return false
  return items.some((item) => !deselectedIds.has(item.id))
}

/** Export workflow package as a single ZIP (agents, prompts, skills, workflow YAML, manifest). */
export function useExportWorkflowPackage() {
  return useMutation({
    mutationFn: async ({
      workflowId,
      workflowName,
      data,
      deselectedIds,
    }: {
      workflowId: string
      workflowName: string
      data: WorkflowDependenciesResponse
      deselectedIds: Set<string>
    }) => {
      const options = {
        include_agents: categoryHasSelection(data.agents, deselectedIds),
        include_agent_dependencies: AGENT_DEP_CATEGORIES.some((cat) =>
          categoryHasSelection(data[cat], deselectedIds)
        ),
        include_workflow_deps: WORKFLOW_DEP_CATEGORIES.some((cat) =>
          categoryHasSelection(data[cat], deselectedIds)
        ),
        include_models: categoryHasSelection(data.models, deselectedIds),
        include_connectors: categoryHasSelection(data.connectors, deselectedIds),
        include_iam: categoryHasSelection(data.iam, deselectedIds),
      }
      const { blob, filename, entityCount, warnings } = await aiworkflowsApi.exportWorkflowPackage(
        workflowId,
        options
      )
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
      return { workflowName, filename, entityCount, warnings }
    },
    onSuccess: ({ workflowName, entityCount, warnings }) => {
      const fileLabel = `${entityCount} file${entityCount !== 1 ? 's' : ''}`
      if (warnings.length > 0) {
        toast.warning(
          `Exported package for "${workflowName}" (${fileLabel}). ${warnings.join(' ')}`
        )
        return
      }
      toast.success(`Exported package for "${workflowName}" (${fileLabel})`)
    },
    onError: (error: Error) => {
      toast.error(`Export failed: ${error.message}`)
    },
  })
}

interface SelectiveYamlExportResult {
  name: string
  success: boolean
  error?: string
  permissionDenied?: boolean
}

function selectiveYamlFailure(name: string, error: unknown): SelectiveYamlExportResult {
  return {
    name,
    success: false,
    permissionDenied: isPermissionDeniedError(error),
    error: error instanceof Error ? error.message : 'Export failed',
  }
}

function toastSelectiveYamlExport(summary: {
  total: number
  successful: number
  results: SelectiveYamlExportResult[]
}): void {
  const permissionMessages = [
    ...new Set(
      summary.results
        .filter((row) => row.permissionDenied && row.error)
        .map((row) => row.error as string),
    ),
  ]
  const hardFailures = summary.results.filter((row) => !row.success && !row.permissionDenied)
  if (permissionMessages.length > 0 && hardFailures.length === 0) {
    const exported =
      summary.successful > 0
        ? `Exported ${summary.successful} file${summary.successful === 1 ? '' : 's'}. `
        : ''
    toast.warning(`${exported}${permissionMessages.join(' ')}`)
    return
  }
  if (hardFailures.length === 0) {
    toast.success(
      `Exported ${summary.successful} file${summary.successful > 1 ? 's' : ''} successfully`,
    )
    return
  }
  const permissionNote = permissionMessages.length > 0 ? ` ${permissionMessages.join(' ')}` : ''
  toast.warning(
    `Exported ${summary.successful}/${summary.total} files. ${hardFailures.length} failed.${permissionNote}`,
  )
}

/** Export workflow and selected dependencies as separate YAML files. */
export function useExportWorkflowWithDependencies() {
  return useMutation({
    mutationFn: async ({
      workflowId,
      workflowName,
      selectedDependencies,
    }: {
      workflowId: string
      workflowName: string
      selectedDependencies: Array<{
        id: string
        name: string
        entityType: WorkflowDependencyEntityType
      }>
    }) => {
      const results: SelectiveYamlExportResult[] = []

      // 1) Export the workflow itself
      try {
        const content = await aiworkflowsApi.exportWorkflow(workflowId)
        const filename = generateExportFilename('workflow', workflowName, 'yaml')
        downloadAsFile(content, filename, 'text/yaml')
        results.push({ name: workflowName, success: true })
      } catch (error) {
        results.push(selectiveYamlFailure(workflowName, error))
      }

      // 2) Export each selected dependency
      for (const dep of selectedDependencies) {
        try {
          let content: string
          switch (dep.entityType) {
            case 'agent':
              content = await exportImportApi.exportAgent(dep.id, false)
              break
            case 'model':
              content = await exportImportApi.exportModel(dep.id)
              break
            case 'connector':
              content = await exportImportApi.exportConnector(dep.id, false)
              break
            case 'iam':
              content = await exportImportApi.exportIam(dep.id)
              break
            case 'tool':
              content = await exportImportApi.exportTool(dep.id)
              break
            case 'prompt':
              content = await exportImportApi.exportPrompt(dep.id)
              break
            case 'skill':
              content = await exportImportApi.exportSkill(dep.id)
              break
            case 'knowledge':
              content = await exportImportApi.exportKnowledge(dep.id, true)
              break
            default:
              throw new Error(`Unsupported dependency type: ${dep.entityType}`)
          }
          const filename = generateExportFilename(dep.entityType, dep.name, 'yaml')
          downloadAsFile(content, filename, 'text/yaml')
          results.push({ name: dep.name, success: true })
          await new Promise((resolve) => setTimeout(resolve, 300))
        } catch (error) {
          results.push(selectiveYamlFailure(dep.name, error))
        }
      }

      return {
        total: results.length,
        successful: results.filter((r) => r.success).length,
        failed: results.filter((r) => !r.success).length,
        results,
      }
    },
    onSuccess: (summary) => {
      toastSelectiveYamlExport(summary)
    },
    onError: (error: Error) => {
      toast.error(`Export failed: ${error.message}`)
    },
  })
}

const BULK_DEP_PLURAL_TO_SINGULAR: Record<string, string> = {
  agents: 'agent',
  models: 'model',
  prompts: 'prompt',
  skills: 'skill',
  tools: 'tool',
  knowledge: 'knowledge',
  connectors: 'connector',
  iam: 'iam',
}

function exportEntityDisplayName(
  entity: Record<string, unknown>,
  fallback: string,
): string {
  const name = entity.name
  if (typeof name === 'string' && name.trim()) return name.trim()
  const portableId = entity._portable_id
  if (typeof portableId === 'string' && portableId.trim()) return portableId.trim()
  return fallback
}

interface BulkExportDownload { entityType: string; name: string; yaml: string }

function buildBulkExportDownloads(parsed: {
  workflows?: Array<Record<string, unknown>>
  dependencies?: Record<string, Array<Record<string, unknown>>>
}): BulkExportDownload[] {
  const downloads: BulkExportDownload[] = []
  const workflows = Array.isArray(parsed.workflows) ? parsed.workflows : []
  for (const wf of workflows) {
    if (!wf || typeof wf !== 'object') continue
    downloads.push({
      entityType: 'workflow',
      name: exportEntityDisplayName(wf, 'workflow'),
      yaml: toYAML(wf),
    })
  }

  const deps = parsed.dependencies || {}
  for (const [plural, entities] of Object.entries(deps)) {
    if (!Array.isArray(entities)) continue
    const entityType =
      BULK_DEP_PLURAL_TO_SINGULAR[plural] || plural.replace(/s$/, '')
    for (const entity of entities) {
      if (!entity || typeof entity !== 'object') continue
      downloads.push({
        entityType,
        name: exportEntityDisplayName(entity, entityType),
        yaml: toYAML(entity),
      })
    }
  }
  return downloads
}

async function downloadBulkExportFilesSequentially(
  downloads: BulkExportDownload[],
): Promise<void> {
  for (let i = 0; i < downloads.length; i++) {
    const item = downloads[i]
    const filename = generateExportFilename(item.entityType, item.name, 'yaml')
    downloadAsFile(item.yaml, filename, 'text/yaml')
    if (i < downloads.length - 1) {
      await new Promise((resolve) => setTimeout(resolve, 300))
    }
  }
}

function packageImportWarningText(result: WorkflowPackageImportResult): string {
  return (result.warnings ?? []).map((item) => item.trim()).filter(Boolean).join(' ')
}

function handleWorkflowPackageImportSuccess(
  result: WorkflowPackageImportResult,
  queryClient: QueryClient,
): void {
  if (!result.dry_run && result.success) {
    queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
  }

  const created = result.summary?.created ?? 0
  const failed = result.summary?.failed ?? 0
  const prereqCount = result.prerequisites?.length ?? 0
  const warningText = packageImportWarningText(result)

  if (result.dry_run) {
    if (warningText) {
      toast.warning(`Preview completed with warnings. ${warningText}`)
      return
    }
    toast.info(
      `Preview: ${created} entit${created === 1 ? 'y' : 'ies'} would be imported` +
        (prereqCount > 0 ? `, ${prereqCount} prerequisite${prereqCount === 1 ? '' : 's'} to configure` : '')
    )
    return
  }

  if (!result.success) {
    toast.error(result.errors?.[0] || 'Package import failed. Review the details and try again.')
    return
  }

  if (warningText) {
    toast.warning(`Imported the workflow. ${warningText}`)
    return
  }

  if (failed === 0) {
    toast.success(
      `Package imported: ${created} entit${created === 1 ? 'y' : 'ies'} created` +
        (prereqCount > 0 ? `. ${prereqCount} prerequisite${prereqCount === 1 ? '' : 's'} may need configuration.` : '')
    )
    return
  }

  toast.warning(`Package imported with issues: ${created} created, ${failed} failed`)
}

/** Export multiple workflows (and optional deps) as separate YAML files via bulk-export API */
export function useBulkExportWorkflows() {
  return useMutation({
    mutationFn: async ({
      workflowIds,
      includeDependencies = false,
    }: {
      workflowIds: string[]
      includeDependencies?: boolean
    }) => {
      if (workflowIds.length === 0) {
        throw new Error('No workflows selected for export')
      }

      const content = await aiworkflowsApi.bulkExportWorkflows(
        workflowIds,
        includeDependencies,
      )
      const parsed = parseYAML<{
        workflows?: Array<Record<string, unknown>>
        dependencies?: Record<string, Array<Record<string, unknown>>>
        warnings?: unknown
      }>(content)
      if (!parsed.success || !parsed.data) {
        throw new Error(parsed.error || 'Failed to parse export bundle')
      }

      const downloads = buildBulkExportDownloads(parsed.data)
      if (downloads.length === 0) {
        throw new Error('No workflows found to export')
      }

      await downloadBulkExportFilesSequentially(downloads)

      const workflows = Array.isArray(parsed.data.workflows) ? parsed.data.workflows : []
      const rawWarnings = parsed.data.warnings
      const warnings = Array.isArray(rawWarnings)
        ? rawWarnings.filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
        : []
      return {
        fileCount: downloads.length,
        workflowCount: workflows.length,
        warnings,
      }
    },
    onSuccess: ({ fileCount, workflowCount, warnings }) => {
      const summary =
        `Exported ${fileCount} file${fileCount > 1 ? 's' : ''} (${workflowCount} workflow${workflowCount > 1 ? 's' : ''})`
      if (warnings.length > 0) {
        toast.warning(`${summary}. ${warnings.join(' ')}`)
        return
      }
      toast.success(`${summary} successfully`)
    },
    onError: (error: Error) => {
      toast.error(`Bulk export failed: ${error.message}`)
    },
  })
}

// =============================================================================
// QUICK-FIX (AI Error Analysis)
// =============================================================================

/** Analyze a node execution error and get human-readable quick-fix advice */
export function useAnalyzeError() {
  return useMutation<QuickFixResponse, Error, QuickFixRequest>({
    mutationFn: (data: QuickFixRequest) => aiworkflowsApi.analyzeError(data),
    onError: (error: Error) => {
      toast.error(error?.message || 'Failed to analyze error')
    },
  })
}

/** Import a workflow from an uploaded YAML file */
export function useImportWorkflowFile() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      file,
      projectId,
      dryRun = false,
    }: {
      file: File
      projectId: string
      dryRun?: boolean
    }): Promise<WorkflowImportResult> => {
      return aiworkflowsApi.importWorkflowFile(file, projectId, dryRun)
    },
    onSuccess: (result) => {
      if (!result.dry_run) {
        queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
      }

      const created = Object.values(result.summary).reduce((acc, s) => acc + (s.created || 0), 0)
      const skipped = Object.values(result.summary).reduce((acc, s) => acc + (s.skipped || 0), 0)
      const failed = Object.values(result.summary).reduce((acc, s) => acc + (s.failed || 0), 0)

      if (result.dry_run) {
        const skippedPart = skipped > 0 ? `, ${skipped} would be skipped` : ''
        toast.info(
          `Preview: ${created} would be created${skippedPart}`
        )
      } else if (result.errors && result.errors.length > 0) {
        toast.error(result.errors[0] || 'Error while importing')
      } else if (failed === 0) {
        const skippedPart = skipped > 0 ? `, ${skipped} skipped` : ''
        toast.success(
          `Import complete: ${created} created${skippedPart}`
        )
      } else {
        toast.warning(
          `Import complete with issues: ${created} created, ${skipped} skipped, ${failed} failed`
        )
      }
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to import agent workflows')
        return
      }
      toast.error(`Import failed: ${error.message}`)
    },
  })
}

/**
 * Import a workflow package ZIP (dependencies first, then the workflow).
 * Resolves agent/prompt/skill references so no placeholder entities are created.
 */
export function useImportWorkflowPackage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      file,
      projectId,
      options,
    }: {
      file: File
      projectId: string
      options?: WorkflowPackageImportOptions
    }): Promise<WorkflowPackageImportResult> => {
      return aiworkflowsApi.importWorkflowPackage(file, projectId, options)
    },
    onSuccess: (result) => {
      handleWorkflowPackageImportSuccess(result, queryClient)
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 403) {
        toast.error('You dont have permission to import workflow packages')
        return
      }
      toast.error(`Package import failed: ${error.message}`)
    },
  })
}

/** Import an n8n workflow JSON into AQUA (translate or create) */
export function useImportN8nWorkflow() {
  const queryClient = useQueryClient()

  return useMutation<N8nImportResponse, Error, N8nImportRequest>({
    mutationFn: (payload: N8nImportRequest) => aiworkflowsApi.importN8nWorkflow(payload),
    onSuccess: (result) => {
      if (result.workflow_id) {
        queryClient.invalidateQueries({ queryKey: queryKeys.workflows.lists() })
        toast.success('n8n workflow imported successfully')
      }
      if (result.warnings?.length) {
        const fallbacks = result.warnings.filter((w) => w.level === 'fallback').length
        const lowConf = result.warnings.filter((w) => w.level === 'low_confidence').length
        if (fallbacks > 0 || lowConf > 0) {
          toast.warning(
            `Imported with ${fallbacks} fallback node${fallbacks !== 1 ? 's' : ''} and ${lowConf} low-confidence mapping${lowConf !== 1 ? 's' : ''}. Review flagged nodes.`
          )
        }
      }
    },
    onError: (error: Error) => {
      const ae = error as ApiError
      if (ae.status === 400) {
        toast.error(ae.message || 'Invalid n8n JSON format')
        return
      }
      toast.error(`n8n import failed: ${error.message}`)
    },
  })
}
